/**
 * Profile API Types
 * TypeScript types for the Profile API layer following Bulletproof React pattern
 *
 * Database Tables (Supabase Auto-Generated):
 * - user_profiles -> UserProfileRow, UserProfileUpdate
 * - learner_user_profiles -> LearnerProfileRow, LearnerProfileUpdate
 * - employer_user_profiles -> EmployerProfileRow, EmployerProfileUpdate
 * - platform_user_profiles -> PlatformProfileRow, PlatformProfileUpdate
 * - user_preferences_accessibility -> AccessibilityPreferencesRow, AccessibilityPreferencesUpdate, AccessibilityPreferencesInsert
 * - user_preferences_email -> EmailPreferencesRow, EmailPreferencesUpdate, EmailPreferencesInsert
 * - file_uploads -> FileUploadRow
 */

import { Database } from '@/integrations/supabase/types';

// =============================================================================
// SUPABASE TABLE TYPE ALIASES
// Auto-generated from Database schema - DO NOT manually duplicate
// =============================================================================
//
// Supabase CRUD Types - Standard Export Pattern
//
// These Insert/Update types are exported following Bulletproof React conventions.
// They may not have current consumers but are kept for:
// - API layer completeness (standard Row/Insert/Update triplet pattern)
// - Future mutation implementations as profile features expand
// - Consistent type extraction pattern across the codebase
// - Type-safe mutation functions when implementing create/update operations
//
// The Row types are actively used for query responses.
// Insert types will be used when implementing create mutations.
// Update types will be used when implementing update mutations.
//
// See: SUPABASE_INTEGRATION_PATTERN_GUIDE_V2.md
// =============================================================================

// User Profiles (user_profiles)
export type UserProfileRow = Database['public']['Tables']['user_profiles']['Row'];
export type UserProfileInsert = Database['public']['Tables']['user_profiles']['Insert'];
export type UserProfileUpdate = Database['public']['Tables']['user_profiles']['Update'];

// Learner User Profiles (learner_user_profiles)
export type LearnerProfileRow = Database['public']['Tables']['learner_user_profiles']['Row'];
export type LearnerProfileInsert = Database['public']['Tables']['learner_user_profiles']['Insert'];
export type LearnerProfileUpdate = Database['public']['Tables']['learner_user_profiles']['Update'];

// Employer User Profiles (employer_user_profiles)
export type EmployerProfileRow = Database['public']['Tables']['employer_user_profiles']['Row'];
export type EmployerProfileInsert = Database['public']['Tables']['employer_user_profiles']['Insert'];
export type EmployerProfileUpdate = Database['public']['Tables']['employer_user_profiles']['Update'];

// Platform User Profiles (platform_user_profiles)
export type PlatformProfileRow = Database['public']['Tables']['platform_user_profiles']['Row'];
export type PlatformProfileInsert = Database['public']['Tables']['platform_user_profiles']['Insert'];
export type PlatformProfileUpdate = Database['public']['Tables']['platform_user_profiles']['Update'];

// Accessibility Preferences (user_preferences_accessibility)
export type AccessibilityPreferencesRow = Database['public']['Tables']['user_preferences_accessibility']['Row'];
export type AccessibilityPreferencesInsert = Database['public']['Tables']['user_preferences_accessibility']['Insert'];
export type AccessibilityPreferencesUpdate = Database['public']['Tables']['user_preferences_accessibility']['Update'];

// Email Preferences (user_preferences_email)
export type EmailPreferencesRow = Database['public']['Tables']['user_preferences_email']['Row'];
export type EmailPreferencesInsert = Database['public']['Tables']['user_preferences_email']['Insert'];
export type EmailPreferencesUpdate = Database['public']['Tables']['user_preferences_email']['Update'];

// File Uploads (file_uploads)
// Note: Also exported from portfolio/api/types.ts - this is intentional as both
// features use file uploads (profile avatars, portfolio attachments)
export type FileUploadRow = Database['public']['Tables']['file_uploads']['Row'];
export type FileUploadInsert = Database['public']['Tables']['file_uploads']['Insert'];
export type FileUploadUpdate = Database['public']['Tables']['file_uploads']['Update'];

// =============================================================================
// EXTENDED TYPES WITH JOINS
// =============================================================================

/**
 * UserProfileWithAvatar
 * User profile with optional avatar file data from file_uploads join
 */
export interface UserProfileWithAvatar extends UserProfileRow {
  file_uploads?: FileUploadRow | null;
}

/**
 * UserProfileComplete
 * Full user profile including role-specific profile data
 * Used when fetching complete user context
 */
export interface UserProfileComplete extends UserProfileRow {
  file_uploads?: FileUploadRow | null;
  learner_user_profiles?: LearnerProfileRow | null;
  employer_user_profiles?: EmployerProfileRow | null;
  platform_user_profiles?: PlatformProfileRow | null;
}

// =============================================================================
// API INPUT TYPES
// =============================================================================

/**
 * UpdateProfileInput
 * Input type for updating user profile base data
 */
export interface UpdateProfileInput {
  userId: string;
  name?: string;
  email?: string;
  avatarFileId?: string | null;
  onboardingData?: Record<string, unknown> | null;
}

/**
 * UpdateAccessibilityInput
 * Input type for updating accessibility preferences
 */
export interface UpdateAccessibilityInput {
  userId: string;
  preferences: Partial<Omit<AccessibilityPreferencesRow, 'id' | 'user_id' | 'created_at' | 'updated_at'>>;
}

/**
 * UpdateEmailInput
 * Input type for updating email preferences
 *
 * `ageRange` is optional; when supplied and equal to the under-16 band,
 * non-essential / marketing preferences are forced to false regardless
 * of the values in `preferences` (GDPR Phase 2 high-privacy defaults).
 */
export interface UpdateEmailInput {
  userId: string;
  preferences: Partial<Omit<EmailPreferencesRow, 'id' | 'user_id' | 'created_at' | 'updated_at'>>;
  /** Self-reported age range; used to enforce under-16 email restrictions */
  ageRange?: string | null;
}

/**
 * UpdateAvatarInput
 * Input type for updating user avatar
 */
export interface UpdateAvatarInput {
  userId: string;
  fileId: string | null;
}

/**
 * UpdateLearnerProfileInput
 * Input type for updating learner-specific profile data
 */
export interface UpdateLearnerProfileInput {
  userId: string;
  learningStyle?: string | null;
  cvCompleted?: boolean;
  onboardingCompleted?: boolean;
}

/**
 * UpdateEmployerProfileInput
 * Input type for updating employer-specific profile data
 */
export interface UpdateEmployerProfileInput {
  userId: string;
  employerCompanyRole?: string | null;
  isPrimaryRepresentative?: boolean;
}

/**
 * UpdatePlatformProfileInput
 * Input type for updating platform-specific profile data
 */
export interface UpdatePlatformProfileInput {
  userId: string;
  department?: string | null;
  platformRole?: string | null;
}

// =============================================================================
// DEFAULT CONSTANTS
// =============================================================================

/**
 * Default accessibility preferences for new users
 * All features disabled by default, respecting user choice
 */
export const DEFAULT_ACCESSIBILITY_PREFERENCES: Omit<AccessibilityPreferencesRow, 'id' | 'user_id' | 'created_at' | 'updated_at'> = {
  dyslexia_friendly_font: false,
  high_contrast_mode: false,
  large_text_mode: false,
  reduced_motion: false,
};

/**
 * Default email preferences for new users
 * Essential notifications enabled, marketing disabled by default
 */
export const DEFAULT_EMAIL_PREFERENCES: Omit<EmailPreferencesRow, 'id' | 'user_id' | 'created_at' | 'updated_at'> = {
  email_digest_frequency: 'weekly',
  marketing_emails: false,
  partnership_notifications: true,
  product_updates: true,
  workshop_reminders: true,
};

// =============================================================================
// PRIVACY & GDPR TYPES
// =============================================================================

/**
 * DataSummary
 * Aggregated data counts for privacy tab GDPR compliance display
 * Used to show users what data exists about them in the system
 */
export interface DataSummary {
  /** Account creation date from user_profiles.created_at */
  accountCreated: string | null;
  /** User's display name from user_profiles */
  userName: string | null;
  /** User's email from user_profiles */
  userEmail: string | null;
  /** Self-reported age range from onboarding_data.personal.ageRange */
  ageRange?: string;
  /** User types (learner, employer, platform) */
  userTypes: string[];
  /** Role names assigned to the user */
  roleNames: string[];

  // Learning
  /** Count of workshops where completed_at is not null */
  workshopsCompleted: number;
  /** Count of workshops in progress (no completed_at) */
  workshopsInProgress: number;
  /** Count of achievements earned */
  achievementsEarned: number;
  /** Count of milestones reached */
  milestonesReached: number;

  // Skills & Career
  /** Count of skills tracked */
  skillsTracked: number;
  /** Count of career goals */
  careerGoals: number;
  /** Count of career pathways selected */
  careerPathways: number;

  // Portfolio
  /** Count of documents */
  documentsCount: number;
  /** Count of personal notes */
  notesCount: number;

  // Interactions
  /** Count of user's bookmarked resources */
  resourcesBookmarked: number;
  /** Count of company bookmarks */
  companyBookmarks: number;

  // Communication
  /** Count of emails sent to this user */
  emailsSent: number;
}

// =============================================================================
// USER TYPE CONSTANTS
// =============================================================================

/**
 * User types supported by the platform
 */
export type UserType = 'learner' | 'employer' | 'platform';

/**
 * Array of all user types for iteration
 */
export const USER_TYPES: UserType[] = ['learner', 'employer', 'platform'];
